/**
 * `F5` · `T-53`/`T-54` — La tarjeta de quien está delante, y los dos actos.
 *
 * Lo que la cajera necesita para confirmar y para actuar: el nombre, cuántos
 * sellos lleva, si tiene premio, y **si se le puede sellar ahora mismo**.
 *
 * ── El teléfono no está, y no es un olvido ────────────────────────────────
 * Confirmado por Isaac el 2026-09-22: es entrada y nunca salida. Ni siquiera el
 * que ella acaba de teclear vuelve del servidor. Lo sostiene el motor —el
 * mostrador no tiene `select` sobre `customers.phone`— y esta pantalla no
 * tendría de dónde sacarlo aunque quisiera.
 *
 * ── `R-09`, los dos puntos que dan forma a esto ──────────────────────────
 * **Sellar no pide nada** —lo firma la sesión de turno— y **canjear pide el PIN
 * siempre, con confirmación explícita antes**: qué premio y a quién. Lo segundo
 * cuesta ~2 s y ocurre en una de cada diez operaciones; lo primero tiene que
 * caber en cinco segundos.
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import type { TarjetaEnLaCaja } from '@fialrest/shared'
import { ErrorDeApi, mensajeParaLaPantalla } from '@fialrest/cliente'
import { canjear, sellar } from './api.js'

interface Props {
  readonly tarjeta: TarjetaEnLaCaja
  /** El que enseñó, si llegó por ahí. Lo gasta el canje — 058, y T-54. */
  readonly codigo: string | null
  /** La ficha cambió: la pantalla de arriba se queda con la nueva. */
  readonly alActuar: (ficha: TarjetaEnLaCaja) => void
  readonly alCerrar: () => void
}

/** «a las 4:15 p.m.», en el reloj de quien mira. */
const aLaHora = (iso: string): string =>
  new Date(iso).toLocaleTimeString('es-MX', { hour: 'numeric', minute: '2-digit' })

const alDia = (iso: string): string =>
  new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })

export function Tarjeta({ tarjeta, codigo, alActuar, alCerrar }: Props): ReactNode {
  const {
    nombre, sellos, sellosPorPremio, premios, premioVenceEn, premioId, premioTexto,
    puedeSellarDesde, tieneTarjetaAqui, comoLaEncontre, customerId,
  } = tarjeta

  const [confirmando, setConfirmando] = useState(false)
  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const campoPin = useRef<HTMLInputElement>(null)

  useEffect(() => { if (confirmando) campoPin.current?.focus() }, [confirmando])

  const sellarAhora = async (): Promise<void> => {
    setOcupado(true)
    setError(null)
    setAviso(null)
    try {
      const { ficha, premiosNuevos } = await sellar(customerId)
      alActuar({ ...ficha, comoLaEncontre })
      //  Que le tocó premio se dice en grande: es lo que la cajera tiene que
      //  contarle a la persona que tiene delante, y si no se dice aquí no se
      //  entera nadie hasta la próxima visita.
      if (premiosNuevos > 0) setAviso('¡Le tocó premio! Puedes entregárselo ahora.')
    } catch (falla) {
      setError(mensajeParaLaPantalla(falla, 'No se pudo sellar.'))
    } finally {
      setOcupado(false)
    }
  }

  //  Recibe el premio en vez de leerlo del estado: dentro del panel ya está
  //  elegido —el otro camino no pinta el formulario— y leerlo obligaba a un
  //  `premioId === null` que no puede ocurrir. Una rama inalcanzable tampoco se
  //  puede probar, y acaba siendo un comentario que nadie comprueba.
  const canjearAhora = async (e: FormEvent, premio: string): Promise<void> => {
    e.preventDefault()
    //  El botón ya está deshabilitado sin cuatro dígitos; esto es para quien
    //  mande el formulario por otro camino.
    if (pin.length !== 4) return
    setOcupado(true)
    setError(null)
    try {
      const { ficha } = await canjear(premio, pin, codigo ?? undefined)
      alActuar({ ...ficha, comoLaEncontre })
      setConfirmando(false)
      setPin('')
      setAviso('Premio entregado.')
    } catch (falla) {
      //  El mensaje viene de la API y está escrito para leerse: «Ese PIN no es
      //  correcto», «Ese premio ya se canjeó». Inventar uno aquí sería adivinar.
      setError(falla instanceof ErrorDeApi ? falla.message : 'No se pudo canjear.')
      setPin('')
    } finally {
      setOcupado(false)
    }
  }

  if (confirmando && premioId !== null) {
    return (
      <article className="caja-confirmar" aria-labelledby="confirmar-titulo">
        {/*  `R-09` punto 4 · «Pantalla de confirmación explícita antes de
            canjear: qué premio, a quién». Evita el canje por inercia al
            encadenar toques, que es como se pierde un café sin que nadie lo
            decida. */}
        <h2 className="caja-confirmar__titulo" id="confirmar-titulo">
          Vas a entregar
        </h2>
        <p className="caja-confirmar__premio">{premioTexto ?? 'un premio'}</p>
        <p className="caja-confirmar__quien">a {nombre}</p>

        {codigo === null && (
          <p className="caja-confirmar__sin-codigo">
            Sin su código. Este canje va a quedar marcado como no verificado.
          </p>
        )}

        <form className="caja-confirmar__forma" onSubmit={(e) => { void canjearAhora(e, premioId) }}>
          <label className="caja-confirmar__etiqueta" htmlFor="pin-canje">Tu PIN</label>
          <input
            id="pin-canje" ref={campoPin} className="caja-confirmar__pin"
            type="password" inputMode="numeric" autoComplete="off" maxLength={4} value={pin}
            onChange={(e) => { setPin(e.target.value.replace(/\D/g, '').slice(0, 4)) }}
          />
          {error === null ? null : <p className="caja-confirmar__error" role="alert">{error}</p>}
          <div className="caja-confirmar__botones">
            <button
              className="caja-confirmar__cancelar" type="button"
              onClick={() => { setConfirmando(false); setPin(''); setError(null) }}
            >
              Cancelar
            </button>
            <button
              className="caja-confirmar__aceptar" type="submit"
              disabled={pin.length !== 4 || ocupado}
            >
              {ocupado ? 'Entregando…' : 'Entregar'}
            </button>
          </div>
        </form>
      </article>
    )
  }

  return (
    <article className="caja-tarjeta" aria-labelledby="tarjeta-nombre">
      <h2 className="caja-tarjeta__nombre" id="tarjeta-nombre">{nombre}</h2>

      <p className="caja-tarjeta__saldo">
        {sellosPorPremio === null
          ? `${sellos} ${sellos === 1 ? 'sello' : 'sellos'}`
          : `${sellos} de ${sellosPorPremio} sellos`}
      </p>

      {premios > 0 && (
        <p className="caja-tarjeta__premio">
          {premios === 1 ? 'Tiene un premio' : `Tiene ${premios} premios`}
          {premioVenceEn === null ? '' : `, vence el ${alDia(premioVenceEn)}`}
        </p>
      )}

      {/*  `R-02` · Que no se pueda sellar todavía NO es un error: es la regla de
          las seis horas, y la cajera tiene que poder explicárselo a la persona
          que tiene delante sin leer un mensaje del motor. */}
      {puedeSellarDesde === null ? null : (
        <p className="caja-tarjeta__ventana">
          Ya se le selló hace poco. Puedes volver a sellarle a las {aLaHora(puedeSellarDesde)}.
        </p>
      )}

      {tieneTarjetaAqui ? null : (
        <p className="caja-tarjeta__primera">Primera vez en esta sucursal.</p>
      )}

      {/*  `R-09` · El camino del teléfono es el degradado: nadie demostró tener
          el aparato en la mano. Se dice AQUÍ y no sólo al canjear, porque es
          antes de actuar cuando sirve de algo. */}
      {comoLaEncontre === 'telefono' && (
        <p className="caja-tarjeta__sin-verificar">
          La encontraste por teléfono, sin su código. Confirma que es quien dice ser.
        </p>
      )}

      {aviso === null ? null : <p className="caja-tarjeta__aviso" role="status">{aviso}</p>}
      {error === null ? null : <p className="caja-tarjeta__error" role="alert">{error}</p>}

      <div className="caja-tarjeta__actos">
        <button
          className="caja-tarjeta__sellar" type="button"
          disabled={ocupado || puedeSellarDesde !== null}
          onClick={() => { void sellarAhora() }}
        >
          {ocupado ? 'Sellando…' : 'Sellar'}
        </button>
        {premioId === null ? null : (
          <button
            className="caja-tarjeta__canjear" type="button"
            onClick={() => { setConfirmando(true); setError(null); setAviso(null) }}
          >
            Entregar premio
          </button>
        )}
      </div>

      <button className="caja-tarjeta__cerrar" type="button" onClick={alCerrar}>
        Siguiente cliente
      </button>
    </article>
  )
}
